import { defineStore } from 'pinia';
import { ref } from 'vue';
import { checkoutStepPolicies } from '../../../services/checkoutStepPolicies';
import GtmService from '../../../services/gtm.service';
import { mapCartToVoyadoEngageCart } from '../../../services/mappers/voyadoEngageWebTracking.mapper';
import {
  CheckoutStage,
  CheckoutStages,
  CheckoutStep,
  CheckoutStepOrder,
  CheckoutSteps,
} from '../../../services/models/checkout.models';
import { VoyadoEngageWebTrackingService } from '../../../services/voyadoEngageWebTracking.service';
import { useShoppingCartStore } from '../../shoppingCart/stores/shoppingCartStore';
import { getCart } from '../../../utils/API/Cart/cartAPI';
import { Cart, CartSchema, LineItemModel } from '../../../utils/API/Cart/cartAPISchema';
import { Log } from '../../../utils/API/Logging/LoggingAPI';
import * as CheckoutHelpers from '../../../utils/checkoutHelpers';
import { useCustomerInformationStore } from './customerInformationStore';
import { useDeliveryInformationStore } from './deliveryInformationStore';

export const useCheckoutStateStore = defineStore('checkoutState', () => {
  // State
  const currentCheckoutStep = ref<CheckoutStep>(CheckoutSteps.CustomerInformation);
  const currentCheckoutStage = ref<CheckoutStage>(CheckoutStages.InitialPageLoad);
  const isLoadingCart = ref(false);
  const isLoading = ref(false);
  const receiptPageUrl = ref('');
  const netsEasyApiCheckoutKey = ref('');
  const paymentId = ref('');
  const newItemsAfterVerification = ref<null | Array<LineItemModel>>(null);
  const clickAndCollectEnabled = ref(false);

  // Actions
  function setIsLoading(value: boolean) {
    isLoading.value = value;
  }
  function setIsLoadingCart(value: boolean) {
    isLoadingCart.value = value;
  }
  function setCheckoutStage(stage: CheckoutStage) {
    currentCheckoutStage.value = stage;
  }
  function setCheckoutStep(step: CheckoutStep) {
    currentCheckoutStep.value = step;
  }
  function setNewItemsAfterVerification(items: Array<LineItemModel> | null) {
    newItemsAfterVerification.value = items;
  }
  function setReceiptPageUrl(url: string) {
    receiptPageUrl.value = url;
  }
  function setNetsEasyApiCheckoutKey(key: string) {
    netsEasyApiCheckoutKey.value = key;
  }
  function setPaymentId(id: string) {
    paymentId.value = id;
  }
  function setClickAndCollectEnabled(value: string | boolean) {
    clickAndCollectEnabled.value = value === true || value === 'true';
  }

  /**
   * Marks an order/reservation as having been successfully placed: swaps to
   * the full-view `PlacingOrder` loading stage and clears the cart, delivery
   * and customer information stores. Clearing the cart synchronously tears
   * down most of the cart view, so the stage swap must happen first - the
   * caller then redirects away via `window.location.href`, which doesn't
   * navigate instantly, so without the stage swap the customer would briefly
   * see an emptied cart. Shared by both the click & collect reservation flow
   * and the online Nets Easy payment-completed flow.
   */
  function beginOrderPlacement() {
    setCheckoutStage(CheckoutStages.PlacingOrder);
    useShoppingCartStore().clear();
    useDeliveryInformationStore().clear();
    useCustomerInformationStore().clear();
  }

  async function loadShoppingCart(warehouseCode?: string): Promise<Cart | undefined> {
    try {
      const data = await getCart(warehouseCode);
      const newCart = CartSchema.parse(data.cartDto);
      VoyadoEngageWebTrackingService.trackCart(mapCartToVoyadoEngageCart(newCart));
      return newCart;
    } catch (error) {
      Log({
        LogLevel: 'Error',
        Message: `[checkoutStateStore] Failed to load shopping cart: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }

  /**
   * Re-fetches the cart for a warehouse-aware, read-only inventory re-check —
   * used when the customer toggles order type between "Online" and
   * "Klikk og hent". Does not remove or adjust line items. If the fetch fails,
   * the previously loaded cart is left untouched (loadShoppingCart already
   * logs the failure and resolves to undefined rather than throwing).
   *
   * @param warehouseCode - The click & collect store's warehouse code, or
   * undefined to check inventory against the default online warehouse.
   */
  async function refetchCartForOrderType(warehouseCode?: string): Promise<void> {
    setIsLoading(true);
    try {
      const newCart = await loadShoppingCart(warehouseCode);
      if (newCart) {
        const shoppingCartStore = useShoppingCartStore();
        shoppingCartStore.setCart(newCart);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function goToStage(stage: CheckoutStage): Promise<void> {
    if (stage === CheckoutStages.Checkout) {
      setCheckoutStep(CheckoutSteps.CustomerInformation);
      await useCustomerInformationStore().setFieldsIfLoggedIn();
      GtmService.pushCheckoutEvent(
        CheckoutHelpers.toGoogleAnalyticsEventName(CheckoutSteps.CustomerInformation)
      );
    }
    setCheckoutStage(stage);
  }

  async function goToStep(toStep: CheckoutStep): Promise<void> {
    const validateStep = async (step: CheckoutStep): Promise<boolean> => {
      const policy = checkoutStepPolicies[step];
      if (!policy) {
        setIsLoading(false);
        return false;
      }
      setIsLoading(true);
      let result = false;
      try {
        result = await policy.validate();
      } catch (e) {
        Log({
          LogLevel: 'Error',
          Message: `[checkoutStateStore] Error validating step "${step}": ${e instanceof Error ? e.message : String(e)}`,
        });
      }
      setIsLoading(false);
      return result;
    };

    for (const step of CheckoutStepOrder) {
      if (step === toStep) break;
      const completed = await validateStep(step);
      if (!completed) {
        Log({
          LogLevel: 'Warning',
          Message: `[checkoutStateStore] Prior step "${step}" validation failed when navigating to "${toStep}"`,
        });
        return;
      }
    }

    if (currentCheckoutStep.value === toStep) return;

    const policy = checkoutStepPolicies[toStep];
    if (policy?.onEnter) await policy.onEnter();
    
    GtmService.pushCheckoutEvent(CheckoutHelpers.toGoogleAnalyticsEventName(toStep));
    setCheckoutStep(toStep);
  }

  function logCartAvailabilityDifferences(
    checkoutStep: CheckoutStep,
    firstCart?: Cart,
    secondCart?: Cart | null
  ): void {
    if (!secondCart) {
      Log({
        LogLevel: 'Warning',
        Message: `[checkoutStateStore] Shopping cart is null in step "${checkoutStep}" for paymentId "${paymentId.value}"`,
      });
      return;
    }
    const differences = CheckoutHelpers.getDifferences(firstCart, secondCart);
    if (Object.keys(differences).length > 0) {
      Log({
        LogLevel: 'Warning',
        Message: `[checkoutStateStore] Cart differences in step "${checkoutStep}" for paymentId "${paymentId.value}": ${JSON.stringify(differences)}`,
      });
    }
  }

  return {
    currentCheckoutStep,
    currentCheckoutStage,
    isLoadingCart,
    isLoading,
    receiptPageUrl,
    netsEasyApiCheckoutKey,
    paymentId,
    newItemsAfterVerification,
    clickAndCollectEnabled,
    setIsLoading,
    setIsLoadingCart,
    setCheckoutStage,
    setCheckoutStep,
    setNewItemsAfterVerification,
    setReceiptPageUrl,
    setNetsEasyApiCheckoutKey,
    setPaymentId,
    setClickAndCollectEnabled,
    beginOrderPlacement,
    loadShoppingCart,
    refetchCartForOrderType,
    goToStage,
    goToStep,
    logCartAvailabilityDifferences,
  };
});
